import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { goals } from '@modules/financial-planning/constants/URL'

function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}

class APIFinancialPlanningGoals extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = {}
  }

  _shouldSkipLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    const storedClientId = store.getters['financialPlanning/goalsClientId']
    const sameClient = String(storedClientId) === String(clientId)

    if (!forceUpdate && sameClient) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/goalsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )
      if (!shouldUpdate) {
        return {
          status: 200,
          value: store.getters['financialPlanning/goals'] || {},
        }
      }
    }

    if (store.getters['financialPlanning/goalsLoading'] && sameClient) {
      return {
        status: 200,
        value: store.getters['financialPlanning/goals'] || {},
      }
    }

    return null
  }

  _emptyGoalsPayload() {
    return { items: [], clientId: null, totals: null, sobra: null }
  }

  _handleResponse(response) {
    if (response && response.status === 200) {
      store.dispatch('financialPlanning/setGoals', response.value || {})
    } else {
      store.dispatch('financialPlanning/setGoals', this._emptyGoalsPayload())
    }
  }

  async _executeCarregar(requestKey, requestUrl) {
    store.dispatch('financialPlanning/setGoalsLoading', true)
    this.reqCarregar[requestKey] = con.get(requestUrl)

    try {
      const response = await this.reqCarregar[requestKey]
      this._handleResponse(response)
      return response
    } catch (error) {
      store.dispatch('financialPlanning/setGoals', this._emptyGoalsPayload())
      throw error
    } finally {
      store.dispatch('financialPlanning/setGoalsLoading', false)
      delete this.reqCarregar[requestKey]
    }
  }

  _clearStaleGoalsOnClientChange(clientId) {
    // Ao carregar objetivos de um cliente DIFERENTE do que está no store,
    // esvazia o slice IMEDIATAMENTE (antes da resposta chegar) para que a UI
    // não exiba a contagem/linhas do cliente anterior durante o loading.
    const storedClientId = store.getters['financialPlanning/goalsClientId']
    const hasStored = storedClientId !== null && storedClientId !== undefined
    const sameClient = String(storedClientId) === String(clientId)
    if (hasStored && !sameClient) {
      store.dispatch('financialPlanning/setGoals', this._emptyGoalsPayload())
    }
  }

  async carregar({ clientId, params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    // Chegou aqui = vamos buscar de verdade. Se a troca for de cliente, limpa
    // o slice antes da requisição para não mostrar dado obsoleto na transição.
    this._clearStaleGoalsOnClientChange(clientId)

    const query = getQueryString(params)
    const requestKey = query ? `${clientId}:${query}` : `${clientId}`

    if (this.reqCarregar[requestKey]) {
      return this.reqCarregar[requestKey]
    }

    const progressUrl = query
      ? `${goals.list(clientId)}/progress${query}`
      : `${goals.list(clientId)}/progress`

    const response = await this._executeCarregar(requestKey, progressUrl)

    return response || {}
  }

  async salvar({ clientId, goal } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!goal || typeof goal !== 'object') {
      return {
        status: 400,
        message: 'Dados do objetivo inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(goal),
    }

    const response = await con.post(goals.list(clientId), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addGoal', {
          item: value,
          clientId,
        })
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async atualizar({ clientId, goalId, goal } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!goalId) {
      return {
        status: 404,
        message: 'Objetivo não encontrado.',
      }
    }

    if (!goal || typeof goal !== 'object') {
      return {
        status: 400,
        message: 'Dados do objetivo inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(goal),
    }

    const response = await con.put(goals.detail(clientId, goalId), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/updateGoal', {
          item: value,
          clientId,
        })
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async remover({ clientId, goalId } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!goalId) {
      return {
        status: 404,
        message: 'Objetivo não encontrado.',
      }
    }

    store.dispatch('financialPlanning/setGoalsLoading', true)

    const response = await con.delet(goals.detail(clientId, goalId))

    store.dispatch('financialPlanning/setGoalsLoading', false)

    if (response && response.status === 200) {
      store.dispatch('financialPlanning/removeGoal', {
        goalId,
        clientId,
      })
    }

    return response || {}
  }

  async importar({ clientId, table, file, save = true } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const payload = { save }

    if (Array.isArray(table)) {
      payload.table = table
    }

    if (file) {
      payload.file = file
    }

    const response = await con.post(goals.batch(clientId), payload)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

}

const apiFinancialPlanningGoals = new APIFinancialPlanningGoals()
export { apiFinancialPlanningGoals }
